import { KeyRound, PowerOff, ShieldCheck, ShieldOff } from "lucide-react";
import { useMemo } from "react";

import { useTranslations } from "@/shared/lang/useTranslations";

import { ACCESS_FILTER_ALL } from "../constants/access.constants";
import type { ApiKeysBucket, ApiKeysBucketCounts } from "../hooks/useApiKeysCatalog";
import { AccessKpiStrip, type AccessKpiTile } from "./AccessKpiStrip";

export interface ApiKeysKpiStripProps {
  /** `null` while the catalog loads — every tile then reads `—` instead of asserting four zeros. */
  counts: ApiKeysBucketCounts | null;
  value: ApiKeysBucket;
  onChange: (next: ApiKeysBucket) => void;
  /** Some keys' roles could not be read, so "sin roles" is a lower bound rather than a count. */
  rolesPartiallyUnknown: boolean;
}

/**
 * Total · Activas · Sin roles · Inactivas o expiradas.
 *
 * "Sin roles" is the anomaly worth a tile of its own: authorization is deny-first and a key holds no
 * permission except through a role, so a key with none authorizes nothing at all while still looking
 * perfectly healthy in every other column. When some keys' roles failed to load the tile says so, because a
 * number that silently excludes unknowns reads as complete.
 *
 * "Activas" counts what the gate would actually accept, expiry included — see `resolveApiKeyStatus`.
 */
export function ApiKeysKpiStrip({ counts, value, onChange, rolesPartiallyUnknown }: ApiKeysKpiStripProps) {
  const { t } = useTranslations("settings");

  const tiles = useMemo<AccessKpiTile<ApiKeysBucket>[]>(
    () => [
      {
        key: ACCESS_FILTER_ALL,
        label: t("access_apikeys_kpi_total"),
        value: counts?.all ?? null,
        icon: KeyRound,
        accent: "neutral",
      },
      {
        key: "active",
        label: t("access_apikeys_kpi_active"),
        value: counts?.active ?? null,
        icon: ShieldCheck,
        accent: "success",
      },
      {
        key: "no_roles",
        label: t("access_apikeys_kpi_no_roles"),
        value: counts?.no_roles ?? null,
        meta: rolesPartiallyUnknown
          ? t("access_apikeys_kpi_no_roles_partial")
          : counts && counts.no_roles > 0
            ? t("access_apikeys_kpi_no_roles_meta")
            : undefined,
        icon: ShieldOff,
        accent: "danger",
      },
      {
        key: "not_active",
        label: t("access_apikeys_kpi_not_active"),
        value: counts?.not_active ?? null,
        icon: PowerOff,
        accent: "warning",
      },
    ],
    [t, counts, rolesPartiallyUnknown],
  );

  return (
    <AccessKpiStrip
      ariaLabel={t("access_apikeys_kpi_group_label")}
      tiles={tiles}
      value={value}
      onChange={onChange}
    />
  );
}
